import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

const formatPrice = (price, currency = 'EUR', locale = 'de-DE') => {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(price)
}

function FeedPreview({ products = [], totalCount = 0, loading = false, error = null }) {
  const { t, i18n } = useTranslation('ecommerce')
  const locale = i18n.language || 'de-DE'

  if (loading) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-6">
        <div className="flex items-center justify-center py-8" role="status">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <span className="sr-only">{t('common.loading', 'Loading...')}</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-6">
        <div className="flex items-start gap-3">
          <svg
            className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div>
            <h4 className="text-sm font-medium text-red-800">
              {t('feed.preview.errorTitle', 'Error Loading Preview')}
            </h4>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
      </div>
    )
  }

  if (products.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-6">
        <div className="text-center py-6">
          <svg
            className="mx-auto h-10 w-10 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
            />
          </svg>
          <p className="mt-3 text-sm text-gray-500">
            {t('feed.preview.noProducts', 'No products found in this feed')}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium text-gray-900">
            {t('feed.preview.title', 'Feed Preview')}
          </h4>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-500">
              {t('feed.preview.showing', 'Showing {{shown}} of {{total}}', {
                shown: products.length,
                total: totalCount
              }).replace('{{shown}}', products.length).replace('{{total}}', totalCount)}
            </span>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              {totalCount} {t('feed.preview.products', 'products')}
            </span>
          </div>
        </div>
      </div>

      {/* Product List */}
      <ul className="divide-y divide-gray-200" role="list">
        {products.map((product) => (
          <li key={product.external_id} className="px-4 py-3 flex items-center gap-4">
            <div className="flex-shrink-0 w-12 h-12 rounded bg-gray-100 overflow-hidden">
              <img
                src={product.image_url || '/placeholder-product.svg'}
                alt={product.title}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = '/placeholder-product.svg'
                }}
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {product.title}
              </p>
              <p className="text-sm text-gray-500">
                {formatPrice(product.price, product.currency, locale)}
              </p>
            </div>
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-green-500"
                fill="currentColor"
                viewBox="0 0 20 20"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}

FeedPreview.propTypes = {
  products: PropTypes.arrayOf(PropTypes.shape({
    external_id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    price: PropTypes.number.isRequired,
    currency: PropTypes.string,
    image_url: PropTypes.string,
  })),
  totalCount: PropTypes.number,
  loading: PropTypes.bool,
  error: PropTypes.string,
}

export default FeedPreview
